import { useQueryClient } from '@tanstack/react-query';

import {
    useLicenseController_getAllClientUser,
    useLicenseController_getAllClientWithUser,
    useLicenseController_getAllUser,
    useUserController_DeleteUser,
    useUserController_ImpersonateUser,
    useUserController_InviteUser,
} from '@/services/hooks';
import type { ClientWithLocations, InviteForm } from '@/services/types';

import {
    type DomainMutation,
    type DomainMutationCallbacks,
    ignoreBody,
    toMutationOptions,
    useMappedMutation,
} from '../shared/mutation';
import { type DomainQuery, useMappedQuery } from '../shared/query';

import { type ManagedUser, toManagedUsers } from './managedUsers';

/** The users with access to one client. */
export function useGetClientUsers(
    clientId: number,
    options: { enabled: boolean }
): DomainQuery<ManagedUser[]> {
    return useMappedQuery(useLicenseController_getAllClientUser(clientId, options), toManagedUsers);
}

/** Every user. OptoScale admins only. */
export function useGetAllUsers(options: { enabled: boolean }): DomainQuery<ManagedUser[]> {
    return useMappedQuery(useLicenseController_getAllUser(options), toManagedUsers);
}

function rows(body: ClientWithLocations[]): Record<string, unknown>[] {
    return body.map((client) => ({ ...client }));
}

/** The clients a user can be invited to. Only whether it loaded matters to the page. */
export function useGetInvitableClients(options: {
    enabled: boolean;
}): DomainQuery<Record<string, unknown>[]> {
    return useMappedQuery(useLicenseController_getAllClientWithUser(options), rows);
}

function inviteToApi(invite: InviteForm) {
    return { requestBody: invite };
}

function userIdToApi(userId: number) {
    return { userId };
}

export function useInviteUser(): DomainMutation<InviteForm, void> {
    return useMappedMutation(useUserController_InviteUser(), inviteToApi, ignoreBody);
}

export function useDeleteUser(callbacks?: DomainMutationCallbacks): DomainMutation<number, void> {
    return useMappedMutation(
        useUserController_DeleteUser(toMutationOptions(callbacks, ignoreBody)),
        userIdToApi,
        ignoreBody
    );
}

/** Signs in as another user, clears every cached query and calls `onImpersonating`. */
export function useImpersonateUser(onImpersonating: () => void): DomainMutation<number, void> {
    const queryClient = useQueryClient();
    return useMappedMutation(
        useUserController_ImpersonateUser({
            onSuccess: () => {
                queryClient.invalidateQueries();
                onImpersonating();
            },
        }),
        userIdToApi,
        ignoreBody
    );
}
